// src/pages/Register.jsx
import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../auth/AuthContext";

export default function Register() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth(); // only for Google flow

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(""); // NEW: success message
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleClickStarted = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "#ffffff",
    border: "1.5px solid #e5e0ed",
    borderRadius: "10px",
    color: "#211a35",
    fontSize: "1rem",
    fontFamily: "inherit",
    margin: 0,
    minHeight: "52px",
    boxSizing: "border-box",
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const oauthMs = googleClickStarted.current == null ? null : Math.round(performance.now() - googleClickStarted.current);
      console.info("[sAInthetic auth] Google OAuth completed", { oauthMs });
      const backendStarted = performance.now();
      setGoogleLoading(true);
      try {
        setError("");
        setSuccessMsg(""); // Clear success

        const res = await fetch(`${BASE_URL}/api/auth/google-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });

        const data = await res.json();
        console.info("[sAInthetic auth] Google backend response", { backendMs: Math.round(performance.now() - backendStarted), status: res.status });
        if (!res.ok) throw new Error(data?.error || "Google sign up failed");

        // Google sign‑up logs in immediately, then goes to welcome
        loginSuccess(data.token, data.user);
        localStorage.setItem("firstLogin", "true");
        navigate("/welcome");
      } catch (err) {
        setError(err.message || "Google sign up failed");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setGoogleLoading(false);
      setError("Google sign up failed. Please try again.");
    },
    onNonOAuthError: () => {

      setGoogleLoading(false);
      setError("Google sign up was interrupted. Please try again.");
    },
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccessMsg(""); // Clear previous messages

    if (!email || !password) {
      setError("Enter your email address and password to continue.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);

      const registerRes = await fetch(`${BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const contentType = registerRes.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const text = await registerRes.text();
        throw new Error(
          `Unexpected response from server (status ${
            registerRes.status
          }). ${text.slice(0, 150)}`
        );
      }

      const registerData = await registerRes.json();
      if (!registerRes.ok) {
        throw new Error(registerData?.error || "Registration failed");
      }

      // ✅ NEW: Show email confirmation message - NO AUTO LOGIN
      setSuccessMsg("Your account is created. Check your inbox for the verification link, then return here to sign in and build your first buyer persona.");
      
      // Clear form for better UX

      const upgradePriceId = localStorage.getItem("upgradePriceId");
      if (upgradePriceId) {
        // PREMIUM FLOW – go to Stripe (still works)
        localStorage.removeItem("upgradePriceId");

        const res = await fetch(`${BASE_URL}/api/create-checkout-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            priceId: upgradePriceId,
            customerEmail: email,
          }),
        });

        const sessionContentType = res.headers.get("content-type") || "";
        if (!sessionContentType.includes("application/json")) {
          const text = await res.text();
          throw new Error(
            `Unexpected response from server (status ${
              res.status
            }). ${text.slice(0, 150)}`
          );
        }

        const data = await res.json();
        const url = data?.url;
        if (url) window.location = url;
        else setError("Failed to retrieve payment URL.");
      }
      // FREE FLOW: No auto-login, just show success message
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f7f5fc 0%, #f2effa 100%)",
        padding: "28px 16px",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: "#ffffff",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          border: "1px solid #e8e2f3",
          borderRadius: "22px",
          padding: "38px 32px 30px",
          maxWidth: "440px",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 10,
          boxShadow: "0 20px 60px rgba(48, 34, 83, 0.10)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <img
            src="/sainthetic-mark.svg"
            alt="SocialSage Logo"
            style={{
              width: "48px",
              height: "48px",
              objectFit: "contain",
              marginBottom: "15px",
              boxShadow: "0 4px 12px rgba(255, 217, 69, 0.2)",
            }}
          />
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: 900,
              color: "#211a35",
              margin: "0 0 10px 0",
              background: "linear-gradient(96deg, #211a35 35%, #7041db 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-1px",
            }}
          >
            sAInthetic
          </h1>
          <p style={{ color: "#716b80", fontSize: "1rem", margin: 0 }}>
            Create your free account
          </p>
        </div>

        <div
          role="note"
          style={{
            background: "#f5f0ff",
            border: "1px solid #e5d9ff",
            color: "#59418b",
            padding: "11px 13px",
            borderRadius: "8px",
            fontSize: "0.86rem",
            lineHeight: 1.45,
            margin: "0 0 18px",
            textAlign: "left",
          }}
        >
          <strong style={{ display: "block", marginBottom: 3 }}>First visit may take a little longer</strong>
          sAInthetic is on a free hosting plan. If the app has been inactive, the first connection can take up to a minute while the service wakes up.
        </div>

        <button
          type="button"
          onClick={() => {
            setError("");
            googleClickStarted.current = performance.now();

            setGoogleLoading(true);
            loginWithGoogle();
          }}
          disabled={googleLoading}
          style={{
            width: "100%",
            background: "#fff",
            color: "#171717",
            border: "1.5px solid #e5e7eb",
            borderRadius: "10px",
            padding: "12px 16px",
            fontWeight: 600,
            fontSize: "0.95rem",
            cursor: googleLoading ? "wait" : "pointer",
            opacity: googleLoading ? 0.7 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <img
            src="/google-icon.svg"
            alt="Google logo"
            style={{ width: 18, height: 18 }}
          />
          <span>{googleLoading ? "Connecting to Google…" : "Continue with Google"}</span>
        </button>
        <div style={{ textAlign: "center", margin: "14px 0 18px", color: "#92909b", fontSize: ".85rem" }}>
          Or continue with email
        </div>
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "17px",
            width: "100%",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                color: "#7041db",
                fontSize: "0.88rem",
                fontWeight: 650,
                marginBottom: "8px",
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: "#7041db",
                fontSize: "0.9rem",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              minLength={6}
              required
              style={inputStyle}
            />
          </div>

          {/* NEW: Success message */}
          {successMsg && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#047857",
                padding: "12px 14px",
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
            >
              {successMsg}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#be123c",
                padding: "12px 14px",
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !!successMsg} // Disable when success
            style={{
              width: "100%",
              background: successMsg ? "#6b7280" : "#ffd945", // Gray when success
              color: "#211a35",
              border: "none",
              padding: "15px 20px",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: successMsg ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              marginTop: "4px",
              boxShadow: "0 8px 24px rgba(255, 217, 69, 0.18)",
            }}
          >
            {loading ? "Creating Account..." : successMsg ? "Account Created!" : "Create Account"}
          </button>
        </form>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "22px",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              background: "#e9e4f0",
            }}
          />
          <span style={{ color: "#8a8496", fontSize: "0.85rem" }}>or</span>
          <div
            style={{
              flex: 1,
              height: 1,
              background: "#e9e4f0",
            }}
          />
        </div>



        <p
          style={{
            textAlign: "center",
            color: "#716b80",
            fontSize: "0.9rem",
            marginTop: "25px",
          }}
        >
          Already have an account?{" "}
          <Link
            to="/login"
            style={{
              color: "#7041db",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Sign In
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
